"use client";

import { Card } from "@/components/ui/card";
import { Button } from "../ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowUp } from "lucide-react";
import { PromptProps } from "./prompt-types";
import { useState, useCallback, useEffect } from "react";
import { Capability } from "./prompt-types";
import posthog from "posthog-js";
import { callInference } from "@/lib/playground-utils";
import { Spinner } from "../ui/spinner";
import PromptText from "./prompt-text";
import PromptCaption from "./prompt-caption";
import SuggestedQueries from "./suggested-queries";
import { streamCaptionInference } from "./caption-streaming";

// Import the PromptOptions interface from prompt-caption.tsx
import type { PromptOptions } from "./prompt-caption";
import { CaptionLength } from "./prompt-caption";

function isInferButtonEnabled(
  imagesLoaded: boolean,
  prompt: string,
  needPrompt: boolean
) {
  if (!imagesLoaded) return false;
  if (needPrompt && prompt.trim() == "") return false;
  return true;
}

export default function Prompt(promptProps: PromptProps) {
  const [prompt, setPrompt] = useState("");
  const [promptOptions, setPromptOptions] = useState<PromptOptions>({
    length: CaptionLength.Normal,
  });
  const [capability, setCapability] = useState(Capability.VisualQuery);
  const [inferring, setInferring] = useState(false);
  const [needPrompt, setNeedPrompt] = useState(true);
  const [reasoningEnabled, setReasoningEnabled] = useState(true);

  // Set initial values from props when they become available
  useEffect(() => {
    if (promptProps.initialPrompt) {
      setPrompt(promptProps.initialPrompt);
    }
  }, [promptProps.initialPrompt]);

  useEffect(() => {
    if (promptProps.initialSkill) {
      const skillMap: Record<string, Capability> = {
        'query': Capability.VisualQuery,
        'caption': Capability.Caption,
        'detect': Capability.ObjectDetection,
        'point': Capability.Pointing,
      };
      const mappedSkill = skillMap[promptProps.initialSkill];
      if (mappedSkill) {
        setCapability(mappedSkill);
        setNeedPrompt(mappedSkill !== Capability.Caption);
      }
    }
  }, [promptProps.initialSkill]);

  // Submit inference request
  const handleInfer = useCallback(
    async (query: string) => {
      const {
        imageFile,
        uploadedImage,
        setInferences,
        inferenceUrl,
        variant,
      } = promptProps;

      if (!imageFile || !uploadedImage) return;

      setInferring(true);

      if (capability === Capability.Caption) {
        try {
          await streamCaptionInference({
            inferenceUrl,
            query,
            imageFile,
            uploadedImage,
            promptOptions,
            reasoningEnabled,
            variant,
            setInferences,
          });
        } catch (err) {
          posthog.captureException(err);
        } finally {
          setInferring(false);
        }
        return;
      }

      try {
        const newInference = await callInference(
          inferenceUrl,
          capability,
          query,
          imageFile,
          uploadedImage,
          { ...promptOptions, reasoning: reasoningEnabled },
          variant
        );
        newInference.captionLength = promptOptions.length || "Normal";

        setInferences((prev) => [newInference, ...prev]);
      } catch (err) {
        posthog.captureException(err);
      } finally {
        setInferring(false);
      }
    },
    [promptProps, capability, promptOptions, reasoningEnabled]
  );



  const onTabChange = (value: Capability) => {
    setCapability(value);
    setPromptOptions({ length: CaptionLength.Normal });
    setNeedPrompt(value !== Capability.Caption);
  };

  const inferEnabled = isInferButtonEnabled(
    !!promptProps.imageFile,
    prompt,
    needPrompt
  );

  function handleSuggestedQuerySelect(query: string) {
    setPrompt(query);
    handleInfer(query);
  }

  return (
    <Card className="px-4 pt-4 pb-2 h-full">
      <Tabs
        className="h-full"
        value={capability as string}
        onValueChange={(value) => onTabChange(value as Capability)}
      >
        <div className="flex flex-col h-full">
          <TabsContent value={Capability.VisualQuery as string}>
            <PromptText
              prompt={prompt}
              setPrompt={setPrompt}
              handleSubmit={(query) => handleInfer(query)}
              placeholder="Enter a question..."
            />
          </TabsContent>
          <TabsContent value={Capability.Caption as string}>
            <PromptCaption
              setPromptOptions={setPromptOptions}
              promptOptions={promptOptions}
            />
          </TabsContent>
          <TabsContent value={Capability.Pointing as string}>
            <PromptText
              prompt={prompt}
              setPrompt={setPrompt}
              handleSubmit={(query) => handleInfer(query)}
              placeholder="Enter something to point at..."
            />
          </TabsContent>
          <TabsContent value={Capability.ObjectDetection as string}>
            <PromptText
              prompt={prompt}
              setPrompt={setPrompt}
              handleSubmit={(query) => handleInfer(query)}
              placeholder="Enter objects to detect..."
            />
          </TabsContent>
        </div>
        <div>
          {promptProps.imageFile && needPrompt && (
            <SuggestedQueries
              inferenceUrl={promptProps.inferenceUrl}
              imageData={promptProps.imageFile}
              capability={capability}
              selectSuggestedQuery={handleSuggestedQuerySelect}
            />
          )}
        </div>

        {/* Reasoning toggle - shown above tabs on mobile, inline on desktop */}
        {capability === Capability.VisualQuery && (
          <div className="flex items-center space-x-2 mb-1 lg:hidden">
            <Switch
              id="reasoning-toggle-mobile"
              checked={reasoningEnabled}
              onCheckedChange={setReasoningEnabled}
            />
            <Label
              htmlFor="reasoning-toggle-mobile"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Reasoning
            </Label>
          </div>
        )}

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <TabsList>
              <TabsTrigger value={Capability.VisualQuery}>Query</TabsTrigger>
              <TabsTrigger value={Capability.Caption}>Caption</TabsTrigger>
              <TabsTrigger value={Capability.Pointing}>Point</TabsTrigger>
              <TabsTrigger value={Capability.ObjectDetection}>Detect</TabsTrigger>
            </TabsList>

            {/* Reasoning toggle - shown inline on desktop */}
            {capability === Capability.VisualQuery && (
              <div className="hidden lg:flex items-center space-x-2">
                <Switch
                  id="reasoning-toggle-desktop"
                  checked={reasoningEnabled}
                  onCheckedChange={setReasoningEnabled}
                />
                <Label
                  htmlFor="reasoning-toggle-desktop"
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  Reasoning
                </Label>
              </div>
            )}
          </div>

          <Button disabled={!inferEnabled} onClick={() => handleInfer(prompt)}>
            {inferring ? (
              <Spinner size="small" className="stroke-primary-foreground" />
            ) : (
              <ArrowUp />
            )}
          </Button>
        </div>
      </Tabs>
    </Card>
  );
}
